import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';

import { enqueueSuccessSnackbar } from 'redux/actions/notifierActions';

const useStyles = makeStyles(theme => ({
  closeIcon: {
    position: 'relative',
    right: `-${theme.spacing(2)}px`,
    top: `-${theme.spacing(1)}px`,
  },
  /**
   * Title row: error icon + heading so the dialog reads as a failure, not a plain info modal.
   * Use margin on the icon rather than flex gap — MUI v4's spacing() returns a unitless number,
   * and gap support is uneven in the environments this app still targets.
   */
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  titleIcon: {
    color: theme.palette.error.main,
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
  },
  titleIconWarning: {
    color: theme.palette.warning.main,
  },
  /**
   * Preserve newlines from multi-line statusError messages (e.g. Miro team
   * membership preflight) so the dialog is readable without reformatting.
   */
  errorBody: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  },
  /**
   * Structured (JSX) bodies use real headings/paragraphs, so pre-wrap would
   * double-space around <p> and <strong>.
   */
  errorBodyRich: {
    whiteSpace: 'normal',
  },
  link: {
    color: theme.palette.primary.main,
  },
  /**
   * Header X is the close control. Stretch Copy error so it is the only footer action
   * and easy to hit. Override DialogActions' default child margins so width: 100% is real.
   */
  actions: {
    display: 'block',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  copyButton: {
    width: '100%',
    margin: 0,
  },
}));

/**
 * Matches http(s) URLs so we can turn Miro team settings links (and similar)
 * into clickable anchors inside an otherwise plain-text statusError.
 */
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/**
 * Renders plain statusError text with https URLs as external links.
 * Everything else stays as text so we do not interpret HTML from Firestore.
 *
 * @param {string} text
 * @param {string} linkClassName
 * @returns {React.ReactNode[]}
 */
export const renderStatusErrorWithLinks = (text, linkClassName) => {
  if (!text) {
    return null;
  }

  const parts = String(text).split(URL_REGEX);

  return parts.map((part, index) => {
    if (part.match(/^https?:\/\//)) {
      // Strip trailing punctuation that often sticks to URLs in prose.
      const trimmed = part.replace(/[.,;:)]+$/, '');
      const trailing = part.slice(trimmed.length);

      return (
        <React.Fragment key={`url-${index}`}>
          <a className={linkClassName} href={trimmed} target="_blank" rel="noopener noreferrer">
            {trimmed}
          </a>
          {trailing}
        </React.Fragment>
      );
    }

    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
};

/**
 * Dialog that shows the full `statusError` for a project or deal error stage.
 * Used when the status pill is clicked instead of a Material-UI tooltip, so
 * multi-line messages (team URLs, email lists) remain usable.
 *
 * Local dialog state (not Redux InfoDialog) so Project Card / Deal Card and
 * detail pages do not fight over a single global modal.
 *
 * @param {object} props
 * @param {boolean} [props.isOpen]
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {string} [props.statusError] - plain-text body (and clipboard copy source)
 * @param {'error'|'warning'} [props.severity='error'] - warning uses a yellow icon and Copy (not Copy error)
 * @param {React.ReactNode} [props.children] - optional rich body (bold headings, etc.).
 *   Copy still uses `statusError` so we never copy HTML from Firestore or JSX.
 */
const StatusErrorDialog = ({
  isOpen,
  onClose,
  title,
  statusError,
  children,
  severity = 'error',
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isWarning = severity === 'warning';
  const TitleIcon = isWarning ? WarningIcon : ErrorOutlineIcon;

  const parsedStringBody = useMemo(
    () => renderStatusErrorWithLinks(statusError, classes.link),
    [statusError, classes.link],
  );

  // Prefer structured JSX when a caller needs emphasis; otherwise keep the
  // historical plain-text path (pre-wrap + auto-linked URLs).
  const bodyNodes = children || parsedStringBody;

  const handleCopy = () => {
    copy(statusError || '', {
      onCopy: () => {
        dispatch(
          enqueueSuccessSnackbar(isWarning ? 'Copied to clipboard' : 'Error copied to clipboard'),
        );
      },
      format: 'text/plain',
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle disableTypography className="pb-0">
        <div className="d-flex justify-content-between align-items-start">
          <div className={classes.titleRow}>
            <TitleIcon
              className={`${classes.titleIcon}${isWarning ? ` ${classes.titleIconWarning}` : ''}`}
              aria-hidden="true"
            />
            <Typography variant="h6">{title}</Typography>
          </div>
          <IconButton aria-label="close" onClick={onClose} className={classes.closeIcon}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="pb-2">
        <Typography
          component="div"
          className={children ? `${classes.errorBody} ${classes.errorBodyRich}` : classes.errorBody}
        >
          {bodyNodes}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleCopy}
          color="primary"
          variant="outlined"
          className={classes.copyButton}
        >
          {isWarning ? 'Copy' : 'Copy error'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

StatusErrorDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  statusError: PropTypes.string,
  children: PropTypes.node,
  severity: PropTypes.oneOf(['error', 'warning']),
};

export default StatusErrorDialog;
